import {
  CareBenefitUnitOfMeasure,
  RenewalRejectionReason,
  ManageMembershipRequestType,
  PaymentSchedule,
  RecordingUserFragment,
  CancelReasonType,
  CareSubscriptionPaymentStatus,
  StripeDisputeStatus,
} from 'shared/types/graphql';

export enum ActivityLogModel {
  CarePlanEnrollment = 'CarePlanEnrollment',
  CareEnrollmentPayment = 'CareEnrollmentPayment',
  EnrollmentSupportEvent = 'EnrollmentSupportEvent',
  CareBenefitUsage = 'CareBenefitUsage',
}

type LogItemData<T extends ActivityLogModel> = {
  type: T;
};

export type BenefitUsageData = LogItemData<ActivityLogModel.CareBenefitUsage> & {
  id: string;
  benefitTitle: string;
  quantity?: number | null;
  usageUnitOfMeasure?: CareBenefitUnitOfMeasure | null;
  amount: number;
  usageClinic: {
    id: string;
    name: string;
  };
};

export type EnrollmentEventType = 'MembershipStarted' | 'MembershipEnded' | 'MembershipRenewed' | 'RenewalEmail';

export type EnrollmentEventData = LogItemData<ActivityLogModel.CarePlanEnrollment> & {
  id: string;
  currentPlanTitle?: string;
  previousPlanTitle?: string;
  renewalDate?: Date;
  renewalRejectionReason?: RenewalRejectionReason | null;
  eventType: EnrollmentEventType;
};

export type ActivityLogManageMembershipRequests = Exclude<
  ManageMembershipRequestType,
  | ManageMembershipRequestType.EditBenefit
  | ManageMembershipRequestType.RemoveBenefit
  | ManageMembershipRequestType.Other
  | ManageMembershipRequestType.ManualAdjusment
>;

export type EnrollmentSupportEventData = LogItemData<ActivityLogModel.EnrollmentSupportEvent> & {
  id: string;
  manageMembershipRequestType: ActivityLogManageMembershipRequests;
  amount?: number;
  paymentSchedule?: PaymentSchedule | 'PayInClinic' | null;
  cancelReason?: CancelReasonType | null;
  currentPlanTitle?: string;
  newPlanTitle?: string;
  expirationDate?: Date;
  transferFromPetName?: string;
  transferToPetName?: string;
  note?: string;
};

export enum CareEnrollmentPaymentDataType {
  Payment = 'Payment',
  Refund = 'Refund',
  Dispute = 'Dispute',
}

export type CareEnrollmentPaymentData = LogItemData<ActivityLogModel.CareEnrollmentPayment> & {
  id: string;
  status: CareSubscriptionPaymentStatus | StripeDisputeStatus;
  subType: CareEnrollmentPaymentDataType;
  amount: number;
  paidAt: Date;
  invoiceDate: Date;
  disputeDate?: Date;
  refundDate?: Date;
  lastPaymentAttempt: Date;
  nextPaymentAttempt: Date;
  paymentMethod?: {
    last4?: string | null;
    cardBrand?: string | null;
    expirationMonth?: number | null;
    expirationYear?: number | null;
  } | null;
};

export type LogItemWithData =
  | BenefitUsageData
  | EnrollmentEventData
  | EnrollmentSupportEventData
  | CareEnrollmentPaymentData;

export interface CareActivityLogItem {
  date: Date;
  user?: RecordingUserFragment | null;
  data: LogItemWithData;
}
